import CustomizeDialog from "components/Car/CustomizeDialog";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import DropZone from "components/Dropzone";
import {
  Box,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import useApi from "_api/index";
import { useAuth } from "hooks/useAuth";
import { size } from "lodash";
import { Image } from "antd";

const ModalSendSlipInvoice = ({
  companyId,
  open,
  onClose,
  fetchData = () => {},
  isEdit = false,
  bill = null,
}) => {
  const { role } = useAuth();
  const [slipFile, setSlipFile] = useState([]);
  const [initialFileUrl, setInitialFileUrl] = useState([]);
  const [error, setError] = useState(null);
  const [statusSlip, setStatusSlip] = useState("");

  useEffect(() => {
    if (!open) {
      setSlipFile([]); // Reset the slip file when modal closes
      setInitialFileUrl([]); // Reset initial file URLs
      setError(null); // Clear any existing errors
      setStatusSlip(""); // Reset status slip
      // Perform any necessary actions when the modal opens
    } else {
      getSlipFile(); // Fetch slip file when modal opens
      console.log("Bill Data:", bill);
      if (isEdit && bill?.status_slip) {
        setStatusSlip(
          bill.status_slip === "ชำระแล้ว" ? "อนุมัติ" : "ไม่อนุมัติ"
        );
      }
    }
  }, [open]);

  const getSlipFile = async () => {
    try {
      const response = await useApi.super_admin.getSlipBill(bill.id);
      if (response.isStatus && response.data) {
        let initial = response.data.map((item) => ({
          id: item.id,
          picture: item.slip,
        }));
        initial = initial.filter((item) => item.picture);
        setInitialFileUrl(initial);
      }
    } catch (error) {
      console.error("Error fetching slip file:", error);
      setError("ไม่สามารถดึงข้อมูลหลักฐานการชำระเงินได้");
    }
  };

  const handleSendSlip = async () => {
    if (slipFile.length > 0) {
      console.log("Sending slip file:", slipFile[0]);
      const file = slipFile[0]?.file;
      await useApi.super_admin
        .uploadSlipBill(bill.id, file)
        .then((res) => {
          if (res) {
            onClose();
          }
        })
        .catch((error) => {
          console.error("Error uploading slip:", error);
          setError("เกิดข้อผิดพลาดในการส่งหลักฐานการชำระเงิน");
        });
    } else {
      setError("กรุณาอัพโหลดหลักฐานการชำระเงิน");
    }
  };

  const handleApproveSlip = async () => {
    if (!statusSlip) {
      setError("กรุณาเลือกผลการตรวจสอบ");
    } else {
      try {
        const response = await useApi.super_admin.approveSlipBill(
          bill.id,
          companyId,
          {
            status_slip: statusSlip,
          }
        );
        if (response.isStatus) {
          onClose();
          fetchData(); // Refresh data after approval
        } else {
          setError("ไม่สามารถอนุมัติหลักฐานการชำระเงินได้");
        }
      } catch (error) {
        console.error("Error approving slip:", error);
        setError("เกิดข้อผิดพลาดในการอนุมัติหลักฐานการชำระเงิน");
      }
    }
  };

  if (!open) return null;

  return (
    <CustomizeDialog
      open={open}
      onClose={onClose}
      title={
        role === "SuperAdminA"
          ? "ตรวจสอบหลักฐานการชำระเงิน"
          : "ส่งหลักฐานการชำระเงิน"
      }
      onSave={role === "SuperAdminA" ? handleApproveSlip : handleSendSlip}
      disabledSave={
        role === "SuperAdminA"
          ? !statusSlip
          : slipFile.length > 0 && slipFile[0]?.file === null
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: 2,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: 8,
            height: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "grey.300",
            borderRadius: 4,
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography>หมายเลขใบแจ้งหนี้</Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={bill ? bill.invoice : ""}
                  InputProps={{
                    readOnly: true,
                    disabled: true,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          {role !== "SuperAdminM" && (
            <>
              <Grid item xs={12}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <Typography>หลักฐานการชำระเงิน</Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <Image.PreviewGroup
                      preview={{
                        getPopupContainer: () => document.body, // Ensure preview renders outside of dialog
                        zIndex: 3000,
                      }}
                    >
                      <Image
                        width={150}
                        height={150}
                        src={initialFileUrl ? initialFileUrl[0]?.picture : ""}
                        alt="Slip Image"
                        style={{ cursor: "pointer" }}
                      />
                    </Image.PreviewGroup>
                  </Grid>
                </Grid>
              </Grid>

              {role !== "superAdminM" && (
                <Grid item xs={12}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <Typography>ผลการตรวจสอบ</Typography>
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Select
                        fullWidth
                        size="small"
                        id="status-select"
                        value={
                          statusSlip ||
                          (bill?.status_slip !== null &&
                          bill?.status_slip === "ชำระแล้ว"
                            ? "อนุมัติ"
                            : "ไม่อนุมัติ")
                        }
                        onChange={(e) => setStatusSlip(e.target.value)}
                      >
                        {["อนุมัติ", "ไม่อนุมัติ"].map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Grid>

        {role !== "SuperAdminA" && (
          <DropZone
            files={slipFile}
            setFiles={setSlipFile}
            initialUrls={initialFileUrl}
            maxImages={1}
            errorMessage="กรุณาอัพโหลดไฟล์ที่ถูกต้อง (สูงสุด 3MB)"
            label="อัพโหลดหลักฐานการชำระเงิน"
            error={error}
            setError={setError}
          />
        )}
      </Box>
    </CustomizeDialog>
  );
};

export default ModalSendSlipInvoice;
